<template>
  <!-- MTH045: post-login promo / new-dish popup. Greets the user once on the
       Order/menu page after a fresh login, mirroring advocado's MenuPromoPopup.
       Content is either a home slide (image, tappable link) or the newest menu
       item (image + name + price). -->
  <transition name="moo-promo-fade">
    <div
      v-if="modelValue && hasContent"
      class="moo-promo-backdrop"
      :style="{ '--mlx-accent': accentColor }"
      @click.self="handleClose"
    >
      <div
        class="moo-promo-card"
        role="dialog"
        aria-modal="true"
        aria-label="Promotion"
      >
        <button
          type="button"
          class="moo-promo-close"
          aria-label="Close"
          @click="handleClose"
        >
          <i class="material-icons-round">close</i>
        </button>

        <div
          class="moo-promo-media"
          :class="{ 'moo-promo-media--tappable': hasLink }"
          @click="handleMediaTap"
        >
          <img :src="image" alt="Promotion" @error="handleImgError" />
        </div>

        <div v-if="title || priceLabel || hasItem" class="moo-promo-body">
          <h2 v-if="title" class="moo-promo-title">{{ title }}</h2>
          <p v-if="priceLabel" class="moo-promo-price">{{ priceLabel }}</p>
          <button
            v-if="hasItem"
            type="button"
            class="moo-promo-cta"
            @click="handleAddToCart"
          >
            Add to cart
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// MTH045: auto-dismiss the promo popup after this many ms if left untouched
// (same behaviour as advocado's MenuPromoPopup, SF014).
const AUTO_CLOSE_MS = 6000;

export default {
  name: "MooMenuPromoPopup",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // A single home slide object: { file_url, link, ... }.
    slide: {
      type: Object,
      default: null,
    },
    // A newest menu item (from get-newest-items). When present the popup shows
    // its name + price + an Add-to-cart button.
    item: {
      type: Object,
      default: null,
    },
    // Theme2 accent colour, passed from the Order screen.
    accentColor: {
      type: String,
      default: "#201a1a",
    },
  },
  emits: ["update:modelValue", "close", "open-link", "add-to-cart"],
  data() {
    return {
      autoCloseTimer: null,
      imgFailed: false,
    };
  },
  watch: {
    modelValue(visible) {
      // Start a fresh countdown each time the popup opens; cancel it whenever
      // the popup closes (X, backdrop, tap).
      this.clearAutoCloseTimer();
      this.imgFailed = false;
      if (visible) {
        this.autoCloseTimer = setTimeout(() => {
          this.handleClose();
        }, AUTO_CLOSE_MS);
      }
    },
  },
  computed: {
    hasItem() {
      return Boolean(this.item && this.item.id);
    },
    image() {
      if (this.hasItem && this.item.image) return this.item.image;
      return (this.slide && this.slide.file_url) || "";
    },
    // A link only applies to a plain promo slide (an item opens the item-detail
    // modal via Add-to-cart instead).
    hasLink() {
      return !this.hasItem && Boolean(this.slide && this.slide.link);
    },
    // Caption — the menu item's name, or the slide's optional title.
    title() {
      if (this.hasItem) return this.item.item_name || "";
      return (this.slide && this.slide.title) || "";
    },
    priceLabel() {
      if (this.hasItem) {
        const memberPrice = parseFloat(this.item.member_price);
        if (memberPrice > 0 && this.item.member_price_format) {
          return this.item.member_price_format;
        }
        return this.item.price_default_format || "";
      }
      return (this.slide && this.slide.price_label) || "";
    },
    hasContent() {
      return Boolean(this.image) && !this.imgFailed;
    },
  },
  methods: {
    clearAutoCloseTimer() {
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
        this.autoCloseTimer = null;
      }
    },
    handleClose() {
      this.clearAutoCloseTimer();
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
    handleMediaTap() {
      if (!this.hasLink) return;
      this.$emit("open-link", this.slide.link);
      this.handleClose();
    },
    handleAddToCart() {
      if (!this.hasItem) return;
      this.$emit("add-to-cart", this.item);
      this.handleClose();
    },
    handleImgError() {
      // Broken image: hide the popup rather than showing an empty card.
      this.imgFailed = true;
      this.handleClose();
    },
  },
  beforeUnmount() {
    this.clearAutoCloseTimer();
  },
};
</script>

<style scoped>
.moo-promo-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(25, 28, 30, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.moo-promo-card {
  position: relative;
  width: 100%;
  max-width: 22rem;
  background-color: #ffffff;
  border-radius: 1.25rem;
  box-shadow: 0 20px 40px rgba(25, 28, 30, 0.18);
  overflow: hidden;
  text-align: center;
}

.moo-promo-close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  z-index: 2;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--mlx-accent, #201a1a);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(25, 28, 30, 0.18);
}

.moo-promo-close .material-icons-round {
  font-size: 1.125rem;
}

.moo-promo-media {
  width: 100%;
  background-color: #f7f4f4;
  overflow: hidden;
  line-height: 0;
}

.moo-promo-media--tappable {
  cursor: pointer;
}

.moo-promo-media img {
  width: 100%;
  height: auto;
  display: block;
}

.moo-promo-body {
  padding: 1rem 1.25rem 1.25rem;
  text-align: center;
}

.moo-promo-title {
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--mlx-accent, #201a1a);
  margin: 0 0 0.35rem;
}

.moo-promo-price {
  font-weight: 600;
  font-size: 0.95rem;
  color: #444;
  margin: 0 0 1rem;
}

.moo-promo-cta {
  width: 100%;
  background-color: var(--mlx-accent, #201a1a);
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  padding: 0.8rem 1rem;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.moo-promo-cta:hover {
  opacity: 0.9;
}

.moo-promo-cta:active {
  transform: scale(0.97);
}

.moo-promo-fade-enter-active,
.moo-promo-fade-leave-active {
  transition: opacity 0.2s ease;
}

.moo-promo-fade-enter-active .moo-promo-card,
.moo-promo-fade-leave-active .moo-promo-card {
  transition: transform 0.2s ease;
}

.moo-promo-fade-enter-from,
.moo-promo-fade-leave-to {
  opacity: 0;
}

.moo-promo-fade-enter-from .moo-promo-card,
.moo-promo-fade-leave-to .moo-promo-card {
  transform: scale(0.95);
}
</style>
